import {
   AdditionalEntitlement,
   HistoricalPrescriptionNotCheckedFault,
   InPersonExaminationRequiredFault,
   MissingRequiredPractitionerContactDataFault,
   P1HandshakeFault,
   PatientContinuouslyUsedMedicineExtendedQuery,
   PatientMedicineQuery,
} from '@dreryk/egabinet-prescription-frontend-api';
import {
   VisitDraftPrescriptionsQuantityQuery,
   VisitPrescriptionListUpdateCommand,
   VisitPrescriptionPackageQuery,
   VisitPrescriptionsCreateResult,
} from '@dreryk/egabinet-visit-frontend-api';

import { refetchCqrsQuery, useCqrsCommand, UseCqrsCommandResult } from '@dreryk/command-query-react-common';
import {
   dispatchSnackbarNegativeEvent,
   dispatchSnackbarPositiveEvent,
   dispatchSnackbarWarningEvent,
} from '@dreryk/react-components';
import { Choice } from '@dreryk/watermelon';

import { CertificateFault, useCertificateFaultHandlerMap } from '~libs.desktop.shared.account';
import {
   isValidatedPrescriptionPackage,
   RecipientCategory,
   SentPrescriptionPackage,
   ValidatedPrescriptionPackage,
} from '~libs.shared.prescription';

import { draftPrescriptionsTranslationKeys } from '../../../../translations';
import { VisitId } from '../../PrescriptionIssuingPanel.types';
import { OnPrescriptionsSent } from '../prescriptions.types';

import { DraftPrescriptionsFieldArray } from './DraftPrescriptionsForm.types';
import { createListDraftPrescriptionDTO, getAdditionalEntitlements } from './DraftPrescriptionsForm.utils';
import { ProAuctoreAndProFamiliaMissingDoctorContactDetails } from './ProAuctoreAndProFamiliaMissingContactDetailsModal/ProAuctoreAndProFamiliaMissingContactDetailsModal.types';

type CqrsVisitPrescriptionListUpdateCommandCommandOptions = {
   onPrescriptionsSent: OnPrescriptionsSent;
   onAuthenticationRequiredFault: VoidFunction;
   onHistoricalPrescriptionNotCheckedFault: (additionalEntitlements: AdditionalEntitlement[]) => void;
   onMissingDoctorContactDetails: (missingDetails: ProAuctoreAndProFamiliaMissingDoctorContactDetails) => void;
   onPatientInPersonExaminationRequired: VoidFunction;
};

type UseCqrsVisitPrescriptionListUpdateCommandFault =
   | CertificateFault
   | HistoricalPrescriptionNotCheckedFault
   | MissingRequiredPractitionerContactDataFault
   | InPersonExaminationRequiredFault
   | P1HandshakeFault;

type CqrsVisitPrescriptionListUpdateCommandCommandParameters = {
   visitId: VisitId;
   sentPrescriptionPackages: SentPrescriptionPackage[];
   draftPrescriptions: DraftPrescriptionsFieldArray;
   prescriptionCategory: RecipientCategory;
};

export const useCqrsVisitPrescriptionListUpdateCommand = (
   options: CqrsVisitPrescriptionListUpdateCommandCommandOptions
): UseCqrsCommandResult<
   VisitPrescriptionsCreateResult,
   CqrsVisitPrescriptionListUpdateCommandCommandParameters,
   UseCqrsVisitPrescriptionListUpdateCommandFault
> => {
   const { onPrescriptionsSent, onAuthenticationRequiredFault } = options;

   return useCqrsCommand(
      (parameters) => {
         const command = new VisitPrescriptionListUpdateCommand();
         command.visitId = parameters.visitId;
         command.prescriptions = createListDraftPrescriptionDTO({
            sentPrescriptionPackages: parameters.sentPrescriptionPackages,
            draftPrescriptions: parameters.draftPrescriptions,
         });
         command.recipientCategory = Choice.of(parameters.prescriptionCategory);
         command.draft = false;
         return command;
      },
      {
         onSuccess: (result) => {
            const prescriptionPackages = result.prescriptionPackages as ValidatedPrescriptionPackage[] | undefined;
            if (prescriptionPackages) {
               onPrescriptionsSent(prescriptionPackages);
               refetchCqrsQuery(PatientMedicineQuery);
               refetchCqrsQuery(PatientContinuouslyUsedMedicineExtendedQuery);
               refetchCqrsQuery(VisitDraftPrescriptionsQuantityQuery);
               refetchCqrsQuery(VisitPrescriptionPackageQuery);

               const validatedPrescriptionPackages = prescriptionPackages.filter((prescriptionPackage) =>
                  isValidatedPrescriptionPackage(prescriptionPackage)
               );

               if (prescriptionPackages.length === validatedPrescriptionPackages.length) {
                  dispatchSnackbarPositiveEvent(draftPrescriptionsTranslationKeys.allPrescriptionsIssued);
               } else if (validatedPrescriptionPackages.length > 0) {
                  dispatchSnackbarWarningEvent(draftPrescriptionsTranslationKeys.partPrescriptionsIssued);
               } else {
                  dispatchSnackbarNegativeEvent(draftPrescriptionsTranslationKeys.prescriptionsNotIssued);
               }
            }
         },
         onFault: {
            ...useCertificateFaultHandlerMap({
               onAuthenticationRequired: onAuthenticationRequiredFault,
            }),
            'egabinet-prescription-frontend-api:HistoricalPrescriptionNotCheckedFault': (_, parameters) => {
               return options.onHistoricalPrescriptionNotCheckedFault(
                  getAdditionalEntitlements(parameters.draftPrescriptions)
               );
            },
            'egabinet-prescription-frontend-api:MissingRequiredPractitionerContactDataFault': (fault) => {
               if (fault.missingAddress && fault.missingPhoneNumber) {
                  options.onMissingDoctorContactDetails(['phoneNumber', 'address']);
                  return;
               }
               options.onMissingDoctorContactDetails(fault.missingAddress ? 'address' : 'phoneNumber');
            },
            'drerykwebPrescription:InPersonExaminationRequiredFault': () => {
               options.onPatientInPersonExaminationRequired();
            },
            'egabinet-prescription-frontend-api:P1HandshakeFault': () => {
               dispatchSnackbarNegativeEvent(draftPrescriptionsTranslationKeys.certificateErrorOccurred);
            },
         },
         onUnexpectedError: () =>
            dispatchSnackbarNegativeEvent(draftPrescriptionsTranslationKeys.prescriptionsNotIssued),
      }
   );
};
